import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { ITaskV2AssignedUser } from '@fi-app/task-v2/fi-task-v2';
import {
    FiDropdownItemAdditionalTextFn,
    FiDropdownItemTitleFn,
} from '@fi-app/shared/fi-select-search/fi-select-search';
import { AppTextService } from '@fi-app/shared/app-text/app-text.service';
import { Subject, takeUntil } from 'rxjs';

interface ITaskV2AddAssigneesDialogData {
    assignedUsers: ITaskV2AssignedUser[];
    availableUsersForAssignee: ITaskV2AssignedUser[];
}

@Component({
    selector: 'fi-task-v2-add-assignees',
    templateUrl: './fi-task-v2-add-assignees-dialog.component.html',
    styleUrls: ['./fi-task-v2-add-assignees-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-task-v2-add-assignees' },
    standalone: false,
})
export class TaskV2AddAssigneesDialogComponent
    extends FiDialogsContentBase<ITaskV2AddAssigneesDialogData, number[]>
    implements OnInit, OnDestroy
{
    protected newlyAddedUserIds: number[] = [];
    private appTextService = inject(AppTextService);

    public ngOnInit(): void {
        this.dialogRef.disableClose = !!this.newlyAddedUserIds.length;
        this.dialogRef
            .backdropClick()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                if (!this.dialogRef.disableClose) {
                    this.dialogRef.close();
                }
            });
    }

    protected get availableUsers(): ITaskV2AssignedUser[] {
        return this.data.availableUsersForAssignee || [];
    }

    protected get assignedUsers(): ITaskV2AssignedUser[] {
        return this.data.assignedUsers || [];
    }

    public isYesButtonDisabled(): boolean {
        return !this.newlyAddedUserIds?.length;
    }

    protected onSelectionChange(newAssigneeIds: number[]): void {
        this.dialogRef.disableClose = !!newAssigneeIds.length;
    }

    public getResult(): number[] {
        return this.newlyAddedUserIds;
    }
    protected getOptionAdditionalClassFn: FiDropdownItemAdditionalTextFn<ITaskV2AssignedUser> = (
        user: ITaskV2AssignedUser,
        allItems: ITaskV2AssignedUser[],
    ) => {
        return !user.IsActivated ? 'red-color' : undefined;
    };

    protected getOptionAdditionalTextFn: FiDropdownItemTitleFn<ITaskV2AssignedUser> = (
        user: ITaskV2AssignedUser,
        allItems: ITaskV2AssignedUser[],
    ) => {
        // todo: remove this once backend is updated
        const isUserActive = user.hasOwnProperty('IsActivated') ? user.IsActivated : true;
        return !isUserActive ? this.appTextService.get('common.deactivated') : undefined;
    };

    protected isOptionDisabledFn = (user: ITaskV2AssignedUser): boolean => {
        // todo: remove this once backend is updated
        const isUserActive = user.hasOwnProperty('IsActivated') ? user.IsActivated : true;
        return this.data.assignedUsers.some((u) => u.Id === user.Id) || !isUserActive;
    };

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
